import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal } from 'atoms/layout/flex';
import { Text14, Title5 } from 'atoms/typography';
import Dialog from 'components/dialog';
import { GraphqlForm, TextField } from 'components/form/Form';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useSnackNotificationContext } from 'contexts/snackNotification';
import useScreenSize from 'hooks/device/useScreenSize';
import { useCreateOrUpdateFiatWallet } from 'hooks/wallets/useCreateOrUpdateFiatWallet';
import { useFiatBalance } from 'hooks/wallets/useFiatBalance';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

const messages = defineMessages({
  title: {
    id: 'Settings.updateName.dialog.title',
    defaultMessage: 'Update my name',
  },
  description: {
    id: 'Settings.updateEmail.dialog.description',
    defaultMessage:
      'We’ll need to verify your new email adress <b>{email}</b> in order to change it',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  gap: var(--double-unit);
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    align-items: stretch;
  }
`;

const Helper = styled(Horizontal).attrs({ gap: 1.5 })`
  border-radius: var(--unit);
  background-color: var(--c-neutral-400);
  padding: var(--intermediate-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const UpdateNameDialog = ({ open, onClose }: Props) => {
  const { update } = useCreateOrUpdateFiatWallet();
  const { showNotification } = useSnackNotificationContext();

  const { canDepositAndWithdraw } = useFiatBalance();
  const { up: isTablet } = useScreenSize('tablet');
  const { fiatWalletAccountable } = useCurrentUserContext();
  const { firstName, lastName } = fiatWalletAccountable || {};
  const [updatedFirstName, setUpdatedFirstName] = useState<string>(firstName!);
  const [updatedLastName, setUpdatedLastName] = useState<string>(lastName!);
  const { formatMessage } = useIntlContext();

  if (!firstName || !lastName) return null;
  const nameHasChanged =
    !!updatedFirstName &&
    !!updatedLastName &&
    (firstName !== updatedFirstName || lastName !== updatedLastName);

  const updateNames = async (onResult: (result: any) => void) => {
    if (nameHasChanged) {
      const res = await update({
        firstName: updatedFirstName,
        lastName: updatedLastName,
      });
      onResult(res);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      title={
        <Title5 className="text-center">
          <FormattedMessage {...messages.title} />
        </Title5>
      }
      fullScreen={!isTablet}
    >
      <StyledGraphqlForm
        onSubmit={(_, onResult) => {
          updateNames(onResult);
        }}
        onSuccess={() => {
          showNotification(
            canDepositAndWithdraw
              ? 'cashWalletNamesUpdatedAskingKycAgain'
              : 'cashWalletNamesUpdatedDefault'
          );
          onClose();
        }}
        render={(Error, SubmitButton) => (
          <>
            {canDepositAndWithdraw && (
              <Helper>
                <FontAwesomeIcon icon={faInfoCircle} />
                <Text14 color="var(--c-neutral-600)">
                  <FormattedMessage
                    id="settings.updateName.dialog.warning"
                    defaultMessage="You will be required to upload an ID matching this change to resume access to cash deposits, withdrawals, and rewards."
                  />
                </Text14>
              </Helper>
            )}
            <TextField
              name="firstName"
              defaultValue={firstName}
              label={formatMessage(glossary.firstName)}
              onChange={e => setUpdatedFirstName(e.target.value)}
            />
            <TextField
              name="lastName"
              defaultValue={lastName}
              label={formatMessage(glossary.lastName)}
              onChange={e => setUpdatedLastName(e.target.value)}
            />
            <Error code />
            <SubmitButton disabled={!nameHasChanged} size="large">
              <FormattedMessage {...glossary.confirm} />
            </SubmitButton>
          </>
        )}
      />
    </Dialog>
  );
};
